import { StyleSheet } from "react-native";
import type { Palette } from "@jackedlogs/shared";

// One StyleSheet factory for the composer family (container, compose form,
// member metric tables, D/W chips, block list). Same dark/light contract as
// makeEditorStyles: the container resolves the palette once and threads the
// styles down. Tap targets ≥44pt per [[feedback_user_visual_accessibility]].
// TODO(cleanup): this factory takes only `Palette`, so every `fontSize` here is
// a hand-literal off the token scale (design-gate: sizes should be `t.sizes.*`).
// Widen to `UiTheme` and tokenize the sizes — a signature change touching the
// two callers (ProgramComposer / PlanComposer). Registered in next/consolidation.md.
export function makeComposerStyles(p: Palette) {
  return StyleSheet.create({
    container: { flex: 1, backgroundColor: p.bg },
    scroll: { flex: 1 },
    scrollContent: { padding: 16, gap: 16, paddingBottom: 48 },

    // The top bar (‹ back · title · Salva) and its 8 style keys moved to the
    // shared <EditorTopBar> primitive (CO-7, 2026-07-15) — box internals now come
    // from <Button>, sizes from the token scale. PlanComposer still renders an
    // error line of its own outside that bar, so errorText stays.
    errorText: { color: p.danger, fontSize: 14, paddingHorizontal: 16, paddingTop: 8 },

    // Persistent warnings line + publish checklist (Follow+Publish §4,
    // warn-only): never colour-only — the ⚠ title carries the meaning,
    // the buttons carry the choice.
    warnToggle: { minHeight: 44, justifyContent: "center" },
    warnToggleRow: { flexDirection: "row", alignItems: "center", gap: 6 },
    checkSheet: {
      marginHorizontal: 16,
      marginTop: 10,
      padding: 12,
      gap: 8,
      borderRadius: 12,
      borderWidth: 1,
      borderColor: p.brand500,
      backgroundColor: p.surface,
    },
    checkTitle: { color: p.ink, fontSize: 15, fontWeight: "700" },
    checkItem: { color: p.ink2, fontSize: 14 },
    checkActions: {
      flexDirection: "row",
      justifyContent: "flex-end",
      gap: 10,
      marginTop: 4,
    },
    checkFixBtn: {
      minHeight: 40,
      paddingHorizontal: 16,
      justifyContent: "center",
      borderRadius: 10,
      borderWidth: 1,
      borderColor: p.border,
    },
    checkFixText: { color: p.ink, fontSize: 14, fontWeight: "700" },
    checkAnywayBtn: {
      minHeight: 40,
      paddingHorizontal: 16,
      justifyContent: "center",
      borderRadius: 10,
      backgroundColor: p.brand500,
    },
    checkAnywayText: { color: p.inkOnBrand, fontSize: 14, fontWeight: "700" },

    // Meta card.
    card: {
      backgroundColor: p.surface,
      borderColor: p.border,
      borderWidth: 1,
      borderRadius: 12,
      padding: 12,
      gap: 10,
    },
    fieldLabel: {
      color: p.ink3,
      fontSize: 12,
      fontWeight: "700",
      letterSpacing: 0.6,
      textTransform: "uppercase",
    },
    input: {
      backgroundColor: p.bg,
      borderColor: p.border,
      borderWidth: 1,
      borderRadius: 8,
      color: p.ink,
      fontSize: 16,
      paddingHorizontal: 10,
      paddingVertical: 8,
      minHeight: 44,
    },
    // Multiline variant for the single-mode (Plan) notes field.
    inputMultiline: { minHeight: 88, textAlignVertical: "top", paddingTop: 8 },
    metaLine: { color: p.ink2, fontSize: 14 },
    metaHint: { color: p.ink3, fontSize: 12 },
    // Lifecycle line in the meta card: status word (left) + the one
    // text action (Pubblica… / Riporta a bozza) right-aligned.
    statusRow: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
    },
    statusAction: { minHeight: 44, justifyContent: "center" },
    statusActionText: { color: p.brand500, fontSize: 14, fontWeight: "700" },
    publishPanel: {
      borderTopWidth: 1,
      borderTopColor: p.border,
      paddingTop: 10,
      gap: 8,
    },

    sectionTitle: {
      color: p.ink3,
      fontSize: 13,
      fontWeight: "700",
      letterSpacing: 0.8,
      textTransform: "uppercase",
    },

    // Compose form.
    addExercisesBtn: {
      minHeight: 40,
      borderRadius: 10,
      borderWidth: 1,
      borderColor: p.brand500,
      alignItems: "center",
      justifyContent: "center",
    },
    addExercisesText: { color: p.brand500, fontSize: 15, fontWeight: "700" },
    editingBanner: {
      color: p.brand500,
      fontSize: 13,
      fontWeight: "700",
    },

    // Member metric table.
    memberCard: {
      backgroundColor: p.surface,
      borderColor: p.border,
      borderWidth: 1,
      borderRadius: 12,
      padding: 12,
      gap: 6,
    },
    memberCardGrouped: { borderColor: p.brand500 },
    memberHead: { flexDirection: "row", alignItems: "center", gap: 8 },
    memberOrdinal: { color: p.brand500, fontSize: 13, fontWeight: "800" },
    memberName: { color: p.ink, fontSize: 16, fontWeight: "600", flex: 1 },
    // The 📝/⇄/✕ member-head + row-level note/remove buttons are the shared
    // <IconButton> primitive (36 box, own hitSlop) — no hand-rolled box here.

    tableHeadRow: { flexDirection: "row", alignItems: "center", gap: 8 },
    // PE-17: the row is fixed action columns + flexible numeric columns. SET,
    // note and remove are fixed at 36 (the <IconButton> box they hold or the
    // spacer that reserves it); REPS and VALUE FLEX so the row always fits the
    // card. Before, all five were fixed and their sum overflowed the card on
    // narrow phones once the legacy note column appeared — pushing the final
    // "–" past the border. Flex (basis 0) divides the leftover width instead,
    // so the action column can never leave the card, whatever the device.
    colSet: { width: 36 },
    // minWidth 44 keeps the numeric fields at the tap-target floor even on
    // sub-phone panes (split-screen / foldable cover / a narrow web-rnw pane);
    // on any real phone (≥320dp) the flex share is well above it, and the
    // clamped minimum row (108 fixed + 32 gaps + 44 + 44 = 228) still fits, so
    // the floor never reintroduces the PE-17 overflow.
    colReps: { flex: 3, minWidth: 44 },
    colValue: { flex: 4, minWidth: 44 },
    // 36 to match the <IconButton> box (note toggle / remove) it holds or
    // reserves as a spacer — the header icon, the note-carrying row's button,
    // and the empty-row spacer then all align in one column (PE-17: was 44,
    // which left the note-row "–" 8px off from the note-less rows).
    colNote: { width: 36, alignItems: "center" },
    colRowRemove: { width: 36, alignItems: "center" },
    // Fixed width for the grouped member-head kind selector — it sits after the
    // flexed exercise name, so it must NOT share the table's flexible colValue
    // (which would fight the name for space); a self-contained column instead.
    // 84 (not wider): KIND_LABELS are ≤4-char canonical abbreviations, and a
    // wider box just eats into the exercise name on narrow superset screens.
    headKindCol: { width: 84 },
    headText: {
      color: p.ink3,
      fontSize: 12,
      fontWeight: "700",
      letterSpacing: 0.4,
    },
    setRow: { flexDirection: "row", alignItems: "center", gap: 8 },
    setNum: { color: p.ink3, fontSize: 14, fontWeight: "600", width: 36 },
    cellInput: {
      backgroundColor: p.bg,
      borderColor: p.border,
      borderWidth: 1,
      borderRadius: 8,
      color: p.ink,
      fontSize: 16,
      fontVariant: ["tabular-nums"],
      paddingHorizontal: 8,
      paddingVertical: 6,
      minHeight: 44,
      textAlign: "center",
    },
    cellInputInvalid: { borderColor: p.danger },
    noteInput: {
      backgroundColor: p.bg,
      borderColor: p.border,
      borderWidth: 1,
      borderRadius: 8,
      color: p.ink,
      fontSize: 14,
      paddingHorizontal: 10,
      paddingVertical: 8,
      minHeight: 44,
    },
    validationText: { color: p.danger, fontSize: 13 },
    addSetBtn: { minHeight: 44, justifyContent: "center" },
    addSetRow: { flexDirection: "row", alignItems: "center", gap: 4 },
    addSetText: { color: p.brand500, fontSize: 14, fontWeight: "700" },

    // D/W chips.
    chipsLabel: { color: p.ink3, fontSize: 12, fontWeight: "700", width: 84 },
    chipsRow: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: 6,
      flex: 1,
    },
    chipsLine: { flexDirection: "row", alignItems: "flex-start", gap: 8 },
    chip: {
      minHeight: 36,
      minWidth: 48,
      paddingHorizontal: 10,
      borderRadius: 10,
      borderWidth: 1,
      borderColor: p.border,
      backgroundColor: p.surface,
      alignItems: "center",
      justifyContent: "center",
    },
    chipOn: { backgroundColor: p.brand500, borderColor: p.brand500 },
    chipText: { color: p.ink2, fontSize: 14, fontWeight: "700" },
    chipTextOn: { color: p.inkOnBrand },

    commitBtn: {
      minHeight: 40,
      borderRadius: 10,
      backgroundColor: p.brand500,
      alignItems: "center",
      justifyContent: "center",
    },
    commitBtnDisabled: { backgroundColor: p.surface2 },
    commitBtnText: { color: p.inkOnBrand, fontSize: 16, fontWeight: "800" },
    commitBtnTextDisabled: { color: p.ink3 },
    cancelEditBtn: { minHeight: 44, alignItems: "center", justifyContent: "center" },
    cancelEditText: { color: p.ink3, fontSize: 14, fontWeight: "600" },
  });
}

export type ComposerStyles = ReturnType<typeof makeComposerStyles>;
